import React, { useContext, useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Grid } from '@material-ui/core'
import { Column } from 'material-table'
import moment from 'moment'

import * as LRS from '../../api/LRS'
import { isDebug } from '../../App.config'
import { getModuleName } from '../../ModuleNames'

import Session from '../../context/SessionContext'
import StudentContext from '../../context/StudentContext'

import { Student, SimutechModule } from '../../interfaces/ModuleTypes'
import { FaultAttemptItem } from '../../api/LRS'
import { FaultState } from '../../interfaces/Types'

import { PrimaryHeader } from '../../styles/Styles'
import DataTile, { DataTileType } from '../dataview/DataTile'
import CriticalError from '../CriticalError'
import LoadingIndicator from '../LoadingIndicator'
import TableView from '../dataview/TableView'


type FaultHistoryItem = {
    id: string
    date: string
    startTime: string
    faultId: string
    faultState: string
    skillRating: number
}

const FaultHistory: React.FC = () => {
    const { currentStudent, setCurrentStudent } = useContext(StudentContext)
    const { user } = useContext(Session)
    const history = useHistory()

    const [error, setError] = useState('')
    const [isDataLoading, setIsDataLoading] = useState(true)
    const [tableData, setTableData] = useState<FaultHistoryItem[]>([])

    const { studentId, modId } = useParams()
    const moduleName = getModuleName(modId)

    useEffect(() => {
        if (currentStudent.userId) return
        isDebug && console.log('Fetch Student effect for Fault History studentId :', studentId)
        const fetchData = async () => {
            const students: Student[] = await LRS.getStudentProfileByUserID(studentId, user.companyId)
                .catch(error => {
                    throw new Error(error)
                })

            setCurrentStudent(students[0])
        }
        fetchData()
            .catch(error => setError(error.toString()))

    }, [currentStudent.userId, setCurrentStudent, studentId, user.companyId])

    useEffect(() => {
        if (!currentStudent.userId || (!isDataLoading)) return
        const fetchData = async () => {
            const faultHistorySummary: FaultAttemptItem[] = await LRS.getFaultAttemptListByUser(currentStudent.userId, user.companyId, modId as SimutechModule)

            const rows: FaultHistoryItem[] = faultHistorySummary.map((faultAttempt: FaultAttemptItem): FaultHistoryItem => {
                const { _id, timestamp, faultId, faultState, skillRating } = faultAttempt

                return {
                    id: _id,
                    date: moment(timestamp).format('YYYY-MM-DD'),
                    startTime: moment(timestamp).format('HH:mm:ss'),
                    faultId: faultId,
                    faultState: faultState === FaultState.NotRepaired ? 'Not Repaired' : faultState,
                    skillRating: skillRating ?? 0
                } as FaultHistoryItem
            })

            setTableData(rows)
            setIsDataLoading(false)
        }
        fetchData()
            .catch(error => setError(error.toString()))

    }, [currentStudent.userId, isDataLoading, modId, user.companyId])

    const selectAttempt = (event: any, rowData: FaultHistoryItem | undefined) => {
        event.preventDefault()
        if (!rowData) return
        history.push(`/individual-data/${studentId}/${modId}/${rowData.id}`)
    }


    if (error) return <CriticalError message={error} />
    if (!currentStudent.userId) return <LoadingIndicator loading={true} text={'Loading fault history...'} />

    const columns: Column<FaultHistoryItem>[] = getColumns()

    return (
        <div>
            <Grid container spacing={4} justify={'flex-start'}>
                <Grid item>
                    <DataTile type={DataTileType.Employee} value={0} text={currentStudent.userName.replace(/,/g, ', ')} />
                </Grid>
            </Grid>

            <PrimaryHeader variant={'h4'}>{moduleName}</PrimaryHeader>
            <TableView
                title={'Fault Attempt History'}
                isLoading={isDataLoading}
                data={tableData}
                columns={columns}
                onRowClick={selectAttempt}
                paging={true}
                search={true}
                searchPlaceholder={'Search by Fault # or State'}
                sorting={true}
            />
        </div>
    )
}

export default FaultHistory

const getColumns = (): Column<FaultHistoryItem>[] => {
    return [
        {
            field: 'id',
            hidden: true,
            searchable: false
        },
        {
            title: 'Date',
            field: 'date',
            type: 'string',
            defaultSort: 'asc',
            searchable: false
        },
        {
            title: 'Start Time',
            field: 'startTime',
            type: 'time',
            searchable: false,
            defaultSort: 'asc'
        },
        {
            title: 'Fault Number',
            field: 'faultId',
            type: 'string',
            searchable: true
        },
        {
            title: 'State',
            field: 'faultState',
            type: 'string',
            searchable: true
        },
        {
            title: 'Skill Rating',
            field: 'skillRating',
            type: 'numeric',
            render: (rowData) => rowData.skillRating === 0 ? '' : rowData.skillRating + '%',
            searchable: false
        },
    ]
}